import { Box, Button, Icon, IconButton } from "@chakra-ui/react";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr";

import { DEFAULT_OPERATION, EMPTY_STATE, sameValueSet } from "@/pages/Buscar/useSearchState";

import { useSearchPage } from "./SearchPageContext";

// "Limpiar" reacts to FILTER state only. Map state (q, city, neighborhood,
// lat, lng, zoom, radius) is managed via the boundary affordances on the map
// ("Dibujar" / "Borrar perímetro" / "Re-centrar") and never by this button.
// This is what stops Limpiar from flickering on every drag.
//
// "Active" means "deviates from the PAGE's defaults" — on /Buscar that is
// Comprar + no filters; on a dedicated page the preset (e.g. a type page's
// house types) counts as not-active so Limpiar stays hidden on cold load.
const isOperationActive = (operation, defaults) => {
    if (!Array.isArray(operation)) return false;
    return !sameValueSet(operation, defaults.operation);
};

export const hasAnyActiveFilter = (state, defaults = EMPTY_STATE) =>
    !sameValueSet(state.type, defaults.type) ||
    isOperationActive(state.operation, defaults) ||
    state.price.length > 0 ||
    state.groundSize.length > 0 ||
    state.bedrooms.length > 0 ||
    state.bathrooms.length > 0 ||
    state.halfBathrooms.length > 0 ||
    state.parkingLots.length > 0 ||
    state.order !== null ||
    state.includeMode !== "both";

// Reset payload shared between Limpiar (filter-only) and the "Ampliar
// búsqueda" CTA in ResultsPane (filter + boundary). Combined with a
// `setFilters` call. Map state (lat/lng/zoom/radius) is intentionally
// untouched. `filterResetPatchFor(defaults)` is the page-aware variant —
// resetting restores the page's preset, not the empty state.
export const FILTER_RESET_PATCH = {
    type: [],
    operation: [DEFAULT_OPERATION],
    price: [],
    groundSize: [],
    bedrooms: [],
    bathrooms: [],
    halfBathrooms: [],
    parkingLots: [],
    order: null,
    includeMode: "both",
};

export const filterResetPatchFor = (defaults = EMPTY_STATE) => ({
    ...FILTER_RESET_PATCH,
    type: defaults.type,
    operation: defaults.operation,
});

const ResetFiltersButton = ({ state, setFilters }) => {
    const searchPage = useSearchPage();
    const defaults = searchPage?.DEFAULTS ?? EMPTY_STATE;
    if (!hasAnyActiveFilter(state, defaults)) return null;

    const onReset = () => {
        // Reset filter state only. Map state (q/city/neighborhood/lat/lng/
        // zoom/radius) is owned by the boundary affordances, never cleared
        // here. Operation and type reset to the page's defaults.
        setFilters(filterResetPatchFor(defaults));
    };

    const sharedStyles = {
        h: "40px",
        fontSize: "sm",
        fontWeight: 500,
        bg: "white",
        color: "gray.700",
        borderWidth: "1px",
        borderColor: "gray.200",
        borderRadius: "md",
        _hover: { bg: "gray.50" },
    };

    return (
        <>
            {/* xs–sm (<md): icon-only square button — matches the Más
                filtros trigger's shape and breakpoint so both compact
                triggers stack/wrap symmetrically. */}
            <Box hideFrom="md">
                <IconButton
                    onClick={onReset}
                    aria-label="Limpiar filtros"
                    {...sharedStyles}
                    minW="40px"
                    w="40px"
                    px={0}
                >
                    <Icon boxSize={4}>
                        <ArrowCounterClockwiseIcon weight="bold" />
                    </Icon>
                </IconButton>
            </Box>

            {/* md+: full label with leading icon */}
            <Box hideBelow="md">
                <Button onClick={onReset} {...sharedStyles} px={4}>
                    <Icon mr={2} boxSize={4}>
                        <ArrowCounterClockwiseIcon weight="bold" />
                    </Icon>
                    Limpiar
                </Button>
            </Box>
        </>
    );
};

export default ResetFiltersButton;
